/**
 * Sidebar/Flyout/index.tsx
 * desc: a section of the collapsed sidebar — an icon row that opens the section's links in a flyout beside the rail
 *       (Setup, Pinned)
 * note: Radix' default popover semantics (click / Enter / Space toggle it, Escape and outside clicks close it) plus
 *       hover-open as a mouse convenience (touch has no hover — a tap just toggles). Focus follows the input that
 *       acted: a keyboard or click open lands on the first link (Radix' own auto-focus skips links), a hover open
 *       leaves focus alone; a keyboard close returns focus to the row, a pointer-leave close or a pointer click on
 *       a link leaves it alone.
 */

import { useEffect, useRef, useState, type MouseEvent, type PointerEvent, type ReactNode } from 'react';
import type { Icon } from '../../Icon/typed';
import Popover from '../../Popover/typed';
import { classNames } from '../../../utilities';
import { trackEvent } from '../../../utilities/analytics';

// grace period for the pointer to travel between the row and the flyout
const FLYOUT_CLOSE_DELAY = 150;

interface FlyoutProps {
    // the section's name in analytics ("Setup", "Pinned")
    id: string;
    icon: Icon;
    isActive: boolean;
    label: string;
    children: ReactNode;
}

const Flyout = ({ id, icon: SectionIcon, isActive, label, children }: FlyoutProps) => {
    //*************************************************
    //*****   Data   **********************************
    //*************************************************
    // State
    const [ isOpen, setIsOpen ] = useState(false);
    // Refs
    const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    // which input opened / is closing the flyout — pointer-driven opens and closes must not move focus
    const openedByHover = useRef(false);
    const closedByPointer = useRef(false);

    // Hooks
    useEffect(() => () => clearTimeout(closeTimer.current), []);


    //*************************************************
    //*****   Public Functions   **********************
    //*************************************************
    const handlePointerEnter = (event: PointerEvent<HTMLElement>) => {
        // a touch tap has no hover: its emulated enter must leave the toggle to Radix
        if (event.pointerType === 'touch') return;

        clearTimeout(closeTimer.current);
        // already open (by hover or keyboard): only the pending close is cancelled
        if (isOpen) return;

        openedByHover.current = true;
        closedByPointer.current = false;
        setIsOpen(true);
    };

    const handlePointerLeave = () => {
        // a keyboard-opened flyout holds focus — the pointer passing by must not take it away
        if (!openedByHover.current) return;

        clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => {
            closedByPointer.current = true;
            setIsOpen(false);
        }, FLYOUT_CLOSE_DELAY);
    };

    // choosing a link closes the flyout — a pointer-driven one like a pointer-leave, a keyboard one like Radix
    // intends; clicks on the head, a group label or the padding leave it open. The wrapper that carries this
    // handler is not interactive itself (role "none" — "presentation" under its ARIA 1.1 name; the older spelling
    // trips the tag-over-role lint): it only sees the links' bubbled clicks
    const handleFlyoutClick = (event: MouseEvent<HTMLDivElement>) => {
        if (!(event.target as Element).closest('a[href]')) return;

        clearTimeout(closeTimer.current);
        closedByPointer.current = openedByHover.current;
        setIsOpen(false);
    };

    // Radix drives the popover: the trigger's click / Enter / Space toggle it, Escape and outside clicks close it
    const handleOpenChange = (next: boolean) => {
        clearTimeout(closeTimer.current);
        if (next) {
            openedByHover.current = false;
            closedByPointer.current = false;
        }
        setIsOpen(next);
    };

    // Radix' own auto-focus skips links and would land on the container — a keyboard / click open lands on the
    // first link instead; a hover open leaves focus where it is
    const handleOpenAutoFocus = (event: Event) => {
        event.preventDefault();
        if (openedByHover.current) return;

        // the flyout is still parked off-screen while this runs — focusing must not scroll the page after it
        (event.target as HTMLElement | null)?.querySelector<HTMLElement>('a[href]')?.focus({ preventScroll: true });
    };

    const handleCloseAutoFocus = (event: Event) => {
        if (closedByPointer.current) event.preventDefault();
    };

    const handleRowClick = (event: MouseEvent<HTMLButtonElement>) => {
        trackEvent(`Global: Sidebar - ${id} Toggle (Click)`);
        // Radix toggles the popover on this click — except that a pointer click always follows the hover that
        // already opened it, and must not close it again; a keyboard activation (a click without pointer detail)
        // toggles as Radix intends
        if (isOpen && openedByHover.current && event.detail > 0) event.preventDefault();
    };


    //*************************************************
    //*****   Render   ********************************
    //*************************************************
    return (
        <Popover
            align="start"
            alignOffset={ -4 }
            asChild
            contentClassname="sidebar-flyout"
            open={ isOpen }
            onCloseAutoFocus={ handleCloseAutoFocus }
            onOpenAutoFocus={ handleOpenAutoFocus }
            onOpenChange={ handleOpenChange }
            side="right"
            sideOffset={ 0 }
            content={
                <div className="flyout" role="none" onClick={ handleFlyoutClick } onPointerEnter={ handlePointerEnter } onPointerLeave={ handlePointerLeave }>
                    <div className="flyout-head">{ label }</div>
                    { children }
                </div>
            }
        >
            <button
                type="button"
                className={ classNames('row', { selected: isActive }) }
                aria-label={ label }
                onClick={ handleRowClick }
                onPointerEnter={ handlePointerEnter }
                onPointerLeave={ handlePointerLeave }
            >
                <SectionIcon readOnly tooltip={ null }/>
            </button>
        </Popover>
    );
};

export default Flyout;
